/**
 * Day Change Detection — background task that detects midnight crossover.
 *
 * v1 equivalent: `useCurrentDay` hook (60s interval).
 * v2: runs outside React via BackgroundTaskRunner.
 *
 * Triggers:
 *   - `periodic` (60s) — catches midnight during active use
 *   - `visibility-change` — catches return after sleep/overnight
 *
 * On day change:
 *   1. Updates `dayStore.today` → reactive view invalidation
 *   2. Saves `lastKnownToday` in `setting_local` (per-device)
 *   3. Runs `reindexNewlyArrivedTasks()` for Upcoming → Today transitions
 */

import dayjs from "dayjs"
import { db } from "@/db/schema"
import { StartAtType, StartAtSubType } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import { useDayStore } from "@/background/stores/dayStore"
import type { BackgroundTaskDef } from "@/background/types"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LAST_KNOWN_TODAY_KEY = "#last-known-today"
const SIXTY_SECONDS = 60_000

// ---------------------------------------------------------------------------
// Reindex: Upcoming → Today transitions
// ---------------------------------------------------------------------------

/**
 * When the day rolls over, tasks scheduled for "today" (or earlier) that were
 * in Upcoming yesterday now belong in Today. They enter at the TOP of today's
 * list so the user sees them immediately.
 *
 * Evening tasks get their own "top of evening" position.
 */
async function reindexNewlyArrivedTasks(): Promise<void> {
  const todayEnd = dayjs().endOf("day").valueOf()

  // Find all startAtIndex entries for Default tasks whose startAt ≤ today
  // These are the tasks that should now be in Today (or Evening).
  const entries = await db.startAtIndex
    .where("type")
    .equals(StartAtType.Default)
    .filter((e) => {
      const startMs = parseDbDate(e.startAt)
      return startMs !== undefined && startMs <= todayEnd
    })
    .toArray()

  if (entries.length === 0) return

  // Find the current min index in Today and Evening
  // New arrivals get indexes ABOVE (smaller than) existing tasks
  let minTodayIndex = 0
  let minEveningIndex = 0

  for (const entry of entries) {
    const isEveningTask = entry.subType === StartAtSubType.ThisEvening
    if (isEveningTask) {
      if (entry.index < minEveningIndex) minEveningIndex = entry.index
    } else {
      if (entry.index < minTodayIndex) minTodayIndex = entry.index
    }
  }

  // Check which of these tasks arrived recently (their startAt was in the future
  // as of the previous day). We approximate this by checking the startAt against
  // yesterday's end — if startAt > yesterdayEnd, it's newly arrived.
  const yesterdayEnd = dayjs().subtract(1, "day").endOf("day").valueOf()
  const newArrivals = entries.filter((e) => (parseDbDate(e.startAt) ?? 0) > yesterdayEnd)

  if (newArrivals.length === 0) return

  console.info(
    `[dayChange] Reindexing ${newArrivals.length} newly arrived tasks`,
  )

  // Sort new arrivals by their current index (preserve relative order from Upcoming)
  newArrivals.sort((a, b) => a.index - b.index)

  // Assign new indexes above existing items
  const INDEX_GAP = 1 // gap between newly assigned indexes
  const updates: Array<{ id: string; index: number }> = []

  let nextTodayIndex = minTodayIndex - INDEX_GAP * newArrivals.length
  let nextEveningIndex = minEveningIndex - INDEX_GAP * newArrivals.length

  for (const entry of newArrivals) {
    const isEveningTask = entry.subType === StartAtSubType.ThisEvening
    if (isEveningTask) {
      updates.push({ id: entry.id!, index: nextEveningIndex })
      nextEveningIndex += INDEX_GAP
    } else {
      updates.push({ id: entry.id!, index: nextTodayIndex })
      nextTodayIndex += INDEX_GAP
    }
  }

  // Batch update indexes
  if (updates.length > 0) {
    await db.transaction("rw", db.startAtIndex, async () => {
      for (const upd of updates) {
        await db.startAtIndex.update(upd.id, { index: upd.index })
      }
    })
    console.info(
      `[dayChange] Reindexed ${updates.length} tasks to top of Today/Evening`,
    )
  }
}

// ---------------------------------------------------------------------------
// lastKnownToday helpers
// ---------------------------------------------------------------------------

async function getLastKnownToday(): Promise<string | undefined> {
  try {
    const row = await db.setting_local.get(LAST_KNOWN_TODAY_KEY)
    return row?.value || undefined
  } catch {
    return undefined
  }
}

async function setLastKnownToday(day: string): Promise<void> {
  const timestamp = new Date().toISOString()
  await db.setting_local.put({
    id: LAST_KNOWN_TODAY_KEY,
    value: day,
    device: "local",
    createdAt: timestamp,
    createdBy: "local",
    updatedAt: timestamp,
    updatedBy: "local",
  })
}

// ---------------------------------------------------------------------------
// Task definition
// ---------------------------------------------------------------------------

export const dayChangeDetection: BackgroundTaskDef = {
  id: "day-change-detection",
  description: "Detect midnight crossover and reindex newly arrived tasks",
  priority: 6,
  triggers: ["periodic", "visibility-change"],
  intervalMs: SIXTY_SECONDS,
  initialDelayMs: 2000, // 2s after app start — let DB queries settle first

  async run(_signal) {
    const changed = useDayStore.getState().update()
    if (!changed) return

    const today = useDayStore.getState().today

    // Check if we missed days (e.g. device was off for 3 days)
    const lastKnown = await getLastKnownToday()
    if (lastKnown && lastKnown !== today) {
      const missedDays = dayjs(today).diff(dayjs(lastKnown), "day")
      if (missedDays > 1) {
        console.info(
          `[dayChange] Missed ${missedDays - 1} day(s) since ${lastKnown}`,
        )
      }
    }

    // Reindex tasks that transitioned from Upcoming → Today
    try {
      await reindexNewlyArrivedTasks()
    } catch (error) {
      console.error("[dayChange] Reindex failed:", error)
    }

    // Persist last known day
    await setLastKnownToday(today)
  },
}
